// LaTeX-инструменты формул: разбор в дерево, обратная запись, приведение к СТО (canon), символы для экспликации,
// места переноса (4.6.2.5), громоздкость строчной формулы (4.6.1.1), линейная запись Word (UnicodeMath).
// Без зависимостей от браузера - используется и в тестах (features/math/cli.ts под Node).

// ------------------------------------------------------------------------------------------------ дерево
export type N =
  | { t: 'c'; v: string }                                   // символ: буква, цифра, знак
  | { t: 'sp' }                                             // пробел исходника (при записи опускается)
  | { t: 'cmd'; n: string; opt?: N[]; args: N[][] }         // \команда[опц]{арг}…
  | { t: 'g'; b: N[] }                                      // {группа}
  | { t: 'sup' } | { t: 'sub' }                             // ^ _
  | { t: 'amp' } | { t: 'br' }                              // & \\
  | { t: 'env'; n: string; spec?: string; b: N[] }          // \begin{n}{spec} … \end{n}
  | { t: 'txt'; n: string; s: string }                      // \text{сырой текст}
  | { t: 'lr'; l: string; r: string; b: N[] };              // \left l … \right r

/** Число аргументов команд (остальные - без аргументов). */
const ARGS: Record<string, number> = {
  frac: 2, dfrac: 2, tfrac: 2, cfrac: 2, binom: 2, dbinom: 2, tbinom: 2, overset: 2, underset: 2, stackrel: 2,
  sqrt: 1, vec: 1, hat: 1, bar: 1, dot: 1, ddot: 1, dddot: 1, tilde: 1, widehat: 1, widetilde: 1, overline: 1, underline: 1,
  overrightarrow: 1, overleftarrow: 1, overleftrightarrow: 1, overbrace: 1, underbrace: 1, boxed: 1, mathbf: 1, mathit: 1,
  mathbb: 1, mathcal: 1, mathfrak: 1, mathsf: 1, mathtt: 1, mathscr: 1, boldsymbol: 1, bm: 1, mathrm: 1, operatorname: 1,
  textcolor: 2, color: 1, placeholder: 1, xrightarrow: 1, xleftarrow: 1, xleftrightarrow: 1, xRightarrow: 1,
  xrightleftharpoons: 1, phantom: 1, mathring: 1, check: 1, breve: 1, acute: 1, grave: 1, pmod: 1, mathop: 1,
  underleftarrow: 1, underrightarrow: 1, cancel: 1, bcancel: 1, xcancel: 1, enclose: 2, mathord: 1, mathbin: 1, mathrel: 1,
  hphantom: 1, vphantom: 1, htmlData: 2, class: 2, cssId: 2, 'operatorname*': 1, label: 1, tag: 1, 'tag*': 1,
  hspace: 1, vspace: 1, eqref: 1, ref: 1,
};
/** Команды с необязательным аргументом [..] перед обязательными. */
const OPT = new Set(['sqrt', 'placeholder', 'xrightarrow', 'xleftarrow', 'xleftrightarrow', 'xRightarrow', 'xrightleftharpoons']);
/** Текстовые команды: аргумент - сырой текст. */
const RAW = new Set(['text', 'textrm', 'textup', 'textnormal', 'mbox', 'hbox', 'textbf', 'textit', 'textsf', 'texttt', 'ce', 'pu', 'mathtext']);
/** Размеры скобок (\big( …): игнорируются - Word растягивает скобки сам. */
const SIZERS = new Set(['big', 'Big', 'bigg', 'Bigg', 'bigl', 'bigr', 'Bigl', 'Bigr', 'biggl', 'biggr', 'Biggl', 'Biggr', 'bigm', 'Bigm']);

class Parser {
  i = 0;
  constructor(private s: string) {}
  peek(k = 0) { return this.s[this.i + k]; }
  eof() { return this.i >= this.s.length; }

  /** Имя команды после '\'. */
  cmdName(): string {
    const s = this.s;
    if (this.i >= s.length) return '';
    if (/[A-Za-z]/.test(s[this.i])) {
      let j = this.i;
      while (j < s.length && /[A-Za-z]/.test(s[j])) j++;
      let n = s.slice(this.i, j);
      if (n === 'operatorname' && s[j] === '*') { n = 'operatorname*'; j++; }
      this.i = j;
      return n;
    }
    // \\ \, \; \{ и т. п. - один символ (включая суррогатные пары)
    const cp = s.codePointAt(this.i)!;
    const ch = String.fromCodePoint(cp);
    this.i += ch.length;
    return ch;
  }

  skipSp() { while (!this.eof() && /\s/.test(this.s[this.i])) this.i++; }

  /** Сырой текст группы {…} (для \text, \begin{…}). */
  rawGroup(): string {
    this.skipSp();
    if (this.peek() !== '{') {
      if (this.eof()) return '';
      const cp = this.s.codePointAt(this.i)!;
      const ch = String.fromCodePoint(cp);
      this.i += ch.length;
      return ch;
    }
    let depth = 0;
    const start = this.i + 1;
    for (; this.i < this.s.length; this.i++) {
      const c = this.s[this.i];
      if (c === '\\') { this.i++; continue; }
      if (c === '{') depth++;
      else if (c === '}') { depth--; if (depth === 0) { const r = this.s.slice(start, this.i); this.i++; return r; } }
    }
    return this.s.slice(start);
  }

  /** Аргумент: {…} или один атом. */
  arg(): N[] {
    this.skipSp();
    if (this.eof()) return [];
    if (this.peek() === '{') { this.i++; const b = this.seq('}'); return b; }
    const a = this.atom();
    return a ? [a] : [];
  }

  /** Необязательный аргумент [..] (без вложенных скобок). */
  optArg(): N[] | undefined {
    this.skipSp();
    if (this.peek() !== '[') return undefined;
    this.i++;
    const b = this.seq(']');
    return b;
  }

  /** Разделитель после \left / \right. */
  delim(): string {
    this.skipSp();
    if (this.eof()) return '.';
    if (this.peek() === '\\') {
      this.i++;
      const n = this.cmdName();
      const m: Record<string, string> = {
        '{': '\\{', '}': '\\}', lbrace: '\\{', rbrace: '\\}', '|': '\\|', Vert: '\\|', lVert: '\\|', rVert: '\\|',
        vert: '|', lvert: '|', rvert: '|', lbrack: '[', rbrack: ']', langle: '\\langle', rangle: '\\rangle',
        lceil: '\\lceil', rceil: '\\rceil', lfloor: '\\lfloor', rfloor: '\\rfloor', backslash: '\\backslash',
        lparen: '(', rparen: ')',
      };
      return m[n] ?? ('\\' + n);
    }
    const cp = this.s.codePointAt(this.i)!;
    const ch = String.fromCodePoint(cp);
    this.i += ch.length;
    return ch;
  }

  /** Последовательность до закрывающего символа (} или ]) либо до \right / \end. */
  seq(close?: string): N[] {
    const out: N[] = [];
    while (!this.eof()) {
      const c = this.peek();
      if (close && c === close) { this.i++; return out; }
      if (c === '}' && close !== '}') { this.i++; continue; }          // лишняя скобка
      if (c === '\\') {
        const save = this.i;
        this.i++;
        const n = this.cmdName();
        if (n === 'right' || n === 'mright' || n === 'end') { this.i = save; return out; }
        this.i = save;
      }
      const a = this.atom();
      if (a) out.push(a);
    }
    return out;
  }

  atom(): N | null {
    const c = this.peek();
    if (c === undefined) return null;
    if (/\s/.test(c)) { this.skipSp(); return { t: 'sp' }; }
    if (c === '{') { this.i++; return { t: 'g', b: this.seq('}') }; }
    if (c === '^') { this.i++; return { t: 'sup' }; }
    if (c === '_') { this.i++; return { t: 'sub' }; }
    if (c === '&') { this.i++; return { t: 'amp' }; }
    if (c === '%') { while (!this.eof() && this.peek() !== '\n') this.i++; return null; }  // комментарий
    if (c === '~') { this.i++; return { t: 'cmd', n: ' ', args: [] }; }
    if (c === '\\') {
      this.i++;
      const n = this.cmdName();
      if (!n) return null;
      if (n === '\\' || n === 'newline' || n === 'cr') { this.optArg(); return { t: 'br' }; }
      if (RAW.has(n)) return { t: 'txt', n, s: this.rawGroup() };
      if (n === 'left' || n === 'mleft') {
        const l = this.delim();
        const b = this.seq();
        let r = '.';
        if (!this.eof()) {
          this.i++;                                  // '\'
          this.cmdName();                            // right / mright
          r = this.delim();
        }
        return { t: 'lr', l, r, b };
      }
      if (n === 'right' || n === 'mright') { this.delim(); return null; }
      if (n === 'begin') {
        const name = this.rawGroup().trim();
        let spec: string | undefined;
        if (name === 'array' || name === 'tabular' || name === 'subarray') spec = this.rawGroup();
        if (name === 'alignat' || name === 'alignat*') this.rawGroup();
        const b = this.seq();
        if (!this.eof()) { this.i++; this.cmdName(); this.rawGroup(); }   // \end{name}
        return { t: 'env', n: name, spec, b };
      }
      if (n === 'end') { this.rawGroup(); return null; }
      if (SIZERS.has(n)) return null;
      const node: N = { t: 'cmd', n, args: [] };
      if (OPT.has(n)) { const o = this.optArg(); if (o) node.opt = o; }
      const k = ARGS[n] || 0;
      for (let j = 0; j < k; j++) node.args.push(this.arg());
      return node;
    }
    const cp = this.s.codePointAt(this.i)!;
    const ch = String.fromCodePoint(cp);
    this.i += ch.length;
    return { t: 'c', v: ch };
  }
}

export function parse(latex: string): N[] {
  const p = new Parser(latex || '');
  const out: N[] = [];
  while (!p.eof()) {
    const part = p.seq();
    out.push(...part);
    if (!p.eof()) {
      // одиночные \right / \end без пары - пропустить
      p.i++;
      const n = p.cmdName();
      if (n === 'end') p.rawGroup(); else p.delim();
    }
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ запись
class Out {
  s = '';
  push(x: string) {
    if (!x) return;
    if (/\\[A-Za-z]+$/.test(this.s) && /^[\p{L}\p{N}]/u.test(x)) this.s += ' ';
    this.s += x;
  }
}

function serInto(o: Out, ns: N[]) {
  for (const n of ns) {
    switch (n.t) {
      case 'c': o.push(n.v); break;
      case 'sp': break;
      case 'sup': o.push('^'); break;
      case 'sub': o.push('_'); break;
      case 'amp': o.push('&'); break;
      case 'br': o.push('\\\\'); break;
      case 'g': o.push('{'); serInto(o, n.b); o.push('}'); break;
      case 'txt': o.push('\\' + n.n + '{' + n.s + '}'); break;
      case 'lr': o.push('\\left' + delimOut(n.l)); serInto(o, n.b); o.push('\\right' + delimOut(n.r)); break;
      case 'env':
        o.push('\\begin{' + n.n + '}' + (n.spec !== undefined ? '{' + n.spec + '}' : ''));
        serInto(o, n.b);
        o.push('\\end{' + n.n + '}');
        break;
      case 'cmd':
        o.push('\\' + n.n);
        if (n.opt) { o.push('['); serInto(o, n.opt); o.push(']'); }
        for (const a of n.args) { o.push('{'); serInto(o, a); o.push('}'); }
        break;
    }
  }
}

function delimOut(d: string) { return /^\\[A-Za-z]/.test(d) ? d + ' ' : d; }

export function ser(ns: N[]): string {
  const o = new Out();
  serInto(o, ns);
  return o.s.replace(/\\left(\\[A-Za-z]+) (?=[^A-Za-z0-9])/g, '\\left$1').replace(/\\right(\\[A-Za-z]+) (?=[^A-Za-z0-9]|$)/g, '\\right$1').trim();
}

// ------------------------------------------------------------------------------------------------ справочники
/** Названия функций - прямым шрифтом (4.6.1.10); \tg и др. - макросы MathLive (см. ml.ts). */
export const FUNC_NAMES = ['arcsin', 'arccos', 'arctg', 'arcctg', 'arctan', 'arccot', 'sin', 'cos', 'tg', 'ctg', 'tan',
  'cot', 'sec', 'csc', 'cosec', 'sinh', 'cosh', 'tanh', 'coth', 'sh', 'ch', 'th', 'cth', 'arsh', 'arch', 'arth', 'ln', 'lg',
  'log', 'exp', 'lim', 'max', 'min', 'sup', 'inf', 'det', 'sgn', 'sign', 'arg', 'deg', 'dim', 'ker', 'rank', 'Re', 'Im', 'mod',
  'grad', 'div', 'rot', 'const'];
/** Слова, которые в набранном тексте превращаются в функции автоматически (без риска спутать с переменными). */
const AUTO_FUNCS = new Set(['arcsin', 'arccos', 'arctg', 'arcctg', 'arctan', 'sin', 'cos', 'tg', 'ctg', 'tan', 'cot',
  'ln', 'lg', 'log', 'exp', 'lim', 'max', 'min', 'det', 'sgn', 'sinh', 'cosh', 'tanh']);
/** Функции, для которых в LaTeX нет стандартной команды - в MathLive определены макросами (ml.ts). */
export const MACRO_FUNCS = ['tg', 'ctg', 'arctg', 'arcctg', 'sh', 'ch', 'th', 'cth', 'arsh', 'arch', 'arth', 'sgn', 'cosec',
  'grad', 'rot', 'const'];
const STD_FUNCS = new Set(['sin', 'cos', 'tan', 'cot', 'sec', 'csc', 'sinh', 'cosh', 'tanh', 'coth', 'arcsin', 'arccos',
  'arctan', 'ln', 'lg', 'log', 'exp', 'lim', 'max', 'min', 'sup', 'inf', 'det', 'arg', 'deg', 'dim', 'ker', 'Pr', 'gcd',
  'hom', 'liminf', 'limsup', 'mod', 'Re', 'Im']);

export const GREEK: Record<string, string> = {
  alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ϵ', varepsilon: 'ε', zeta: 'ζ', eta: 'η', theta: 'θ',
  vartheta: 'ϑ', iota: 'ι', kappa: 'κ', varkappa: 'ϰ', lambda: 'λ', mu: 'μ', nu: 'ν', xi: 'ξ', omicron: 'ο', pi: 'π', varpi: 'ϖ',
  rho: 'ρ', varrho: 'ϱ', sigma: 'σ', varsigma: 'ς', tau: 'τ', upsilon: 'υ', phi: 'ϕ', varphi: 'φ', chi: 'χ', psi: 'ψ',
  omega: 'ω', Gamma: 'Γ', Delta: 'Δ', Theta: 'Θ', Lambda: 'Λ', Xi: 'Ξ', Pi: 'Π', Sigma: 'Σ', Upsilon: 'Υ', Phi: 'Φ',
  Psi: 'Ψ', Omega: 'Ω', digamma: 'ϝ',
};
export const GREEK_BY_CHAR: Record<string, string> = Object.fromEntries(
  Object.entries(GREEK).filter(([k]) => !['epsilon', 'phi', 'omicron'].includes(k)).map(([k, v]) => [v, k]));
GREEK_BY_CHAR['ϵ'] = 'epsilon'; GREEK_BY_CHAR['ϕ'] = 'phi'; GREEK_BY_CHAR['µ'] = 'mu';

/** Знаки-команды LaTeX -> символ Юникода (для линейной записи и распознавания). */
export const SYM: Record<string, string> = {
  cdot: '·', times: '×', pm: '±', mp: '∓', div: '÷', ast: '∗', star: '⋆', circ: '∘', bullet: '∙', degree: '°',
  ge: '≥', geq: '≥', le: '≤', leq: '≤', geqslant: '⩾', leqslant: '⩽', ne: '≠', neq: '≠', approx: '≈', equiv: '≡',
  sim: '∼', simeq: '≃', cong: '≅', propto: '∝', ll: '≪', gg: '≫', doteq: '≐', infty: '∞', partial: '∂', nabla: '∇',
  in: '∈', notin: '∉', ni: '∋', subset: '⊂', supset: '⊃', subseteq: '⊆', supseteq: '⊇', cup: '∪', cap: '∩',
  setminus: '∖', forall: '∀', exists: '∃', nexists: '∄', emptyset: '∅', varnothing: '∅', neg: '¬', lnot: '¬',
  lor: '∨', vee: '∨', land: '∧', wedge: '∧', oplus: '⊕', otimes: '⊗', to: '→', rightarrow: '→', leftarrow: '←',
  gets: '←', leftrightarrow: '↔', Rightarrow: '⇒', Leftarrow: '⇐', Leftrightarrow: '⇔', implies: '⇒', iff: '⇔',
  longrightarrow: '⟶', longleftarrow: '⟵', Longrightarrow: '⟹', Longleftrightarrow: '⟺', uparrow: '↑',
  downarrow: '↓', updownarrow: '↕', mapsto: '↦', rightleftarrows: '⇄', leftrightarrows: '⇆', rightleftharpoons: '⇌',
  nearrow: '↗', searrow: '↘', ldots: '…', dots: '…', cdots: '⋯', vdots: '⋮', ddots: '⋱', prime: '′', angle: '∠',
  measuredangle: '∡', triangle: '△', perp: '⊥', parallel: '∥', hbar: 'ℏ', ell: 'ℓ', Re: 'ℜ', Im: 'ℑ', aleph: 'ℵ',
  mid: '∣', dagger: '†', top: '⊤', bot: '⊥', langle: '⟨', rangle: '⟩', square: '□', blacksquare: '■', checkmark: '✓',
  diamond: '⋄', therefore: '∴', because: '∵', lceil: '⌈', rceil: '⌉', lfloor: '⌊', rfloor: '⌋', wp: '℘',
  sum: '∑', prod: '∏', coprod: '∐', int: '∫', iint: '∬', iiint: '∭', oint: '∮', bigcup: '⋃', bigcap: '⋂',
  bigvee: '⋁', bigwedge: '⋀', bigoplus: '⨁', bigotimes: '⨂', backslash: '∖', smallsetminus: '∖', complement: '∁',
};
/** Символ Юникода -> команда LaTeX (обратно). */
export const SYM_BY_CHAR: Record<string, string> = {};
for (const [k, v] of Object.entries(SYM)) if (!(v in SYM_BY_CHAR)) SYM_BY_CHAR[v] = k;
Object.assign(SYM_BY_CHAR, {
  '≥': 'ge', '≤': 'le', '≠': 'ne', '→': 'to', '∅': 'emptyset', '¬': 'neg', '∨': 'lor', '∧': 'land', '⇒': 'Rightarrow',
  '⇔': 'Leftrightarrow', '…': 'ldots', '⋅': 'cdot', '∙': 'cdot', '−': '-', '–': '-', '⊥': 'perp', '∖': 'setminus',
  '←': 'leftarrow', '⋯': 'ldots',
});

const NARY = new Set(['sum', 'prod', 'coprod', 'int', 'iint', 'iiint', 'oint', 'bigcup', 'bigcap', 'bigvee', 'bigwedge',
  'bigoplus', 'bigotimes']);
const ACCENT = new Set(['vec', 'hat', 'bar', 'dot', 'ddot', 'dddot', 'tilde', 'widehat', 'widetilde', 'overline',
  'overrightarrow', 'mathring', 'check', 'breve', 'acute', 'grave', 'underline']);
const REL = new Set(['=', '<', '>', '≈', '≤', '≥', '≠', '≡', '∼', '≅', '∝', '→', '⇒', '⇔', '⇄', '⇌', '←', '↔', '⩽', '⩾']);
const REL_CMDS = new Set(['approx', 'le', 'leq', 'ge', 'geq', 'ne', 'neq', 'equiv', 'sim', 'simeq', 'cong', 'propto',
  'to', 'rightarrow', 'Rightarrow', 'Leftrightarrow', 'iff', 'implies', 'leqslant', 'geqslant', 'rightleftarrows',
  'rightleftharpoons', 'longrightarrow', 'leftarrow', 'gt', 'lt', 'll', 'gg', 'in', 'subset', 'subseteq']);

// ------------------------------------------------------------------------------------------------ приведение к СТО
export interface CanonNote { code: string; msg: string }

/** Обход списка узлов с заменой: fn возвращает узел(ы) или undefined (оставить как есть, обойти детей). */
function mapNodes(ns: N[], fn: (n: N, i: number, arr: N[]) => N | N[] | null | undefined): N[] {
  const out: N[] = [];
  for (let i = 0; i < ns.length; i++) {
    const r = fn(ns[i], i, ns);
    if (r === null) continue;
    if (r !== undefined) { if (Array.isArray(r)) out.push(...r); else out.push(r); continue; }
    out.push(deepMap(ns[i], fn));
  }
  return out;
}

function deepMap(n: N, fn: Parameters<typeof mapNodes>[1]): N {
  switch (n.t) {
    case 'g': return { ...n, b: mapNodes(n.b, fn) };
    case 'lr': return { ...n, b: mapNodes(n.b, fn) };
    case 'env': return { ...n, b: mapNodes(n.b, fn) };
    case 'cmd': return { ...n, opt: n.opt ? mapNodes(n.opt, fn) : undefined, args: n.args.map((a) => mapNodes(a, fn)) };
    default: return n;
  }
}

const c = (v: string): N => ({ t: 'c', v });
const cmd = (n: string, ...args: N[][]): N => ({ t: 'cmd', n, args });
const isLetter = (n: N | undefined) => !!n && n.t === 'c' && /^[A-Za-z]$/.test(n.v);
const isDigit = (n: N | undefined) => !!n && n.t === 'c' && /^[0-9]$/.test(n.v);

/** Окружения-«обёртки» из документов LaTeX: снять (одна строка) или превратить в aligned (несколько). */
const WRAP_ENVS = new Set(['equation', 'equation*', 'displaymath', 'math', 'align', 'align*', 'gather', 'gather*',
  'multline', 'multline*', 'eqnarray', 'eqnarray*', 'flalign', 'flalign*', 'alignat', 'alignat*']);

/**
 * Приведение формулы к виду, который одинаково понимают MathLive, writer/omml.py и СТО:
 * - диалекты: $…$, $$…$$, \[…\], \(…\), equation/align (→ aligned), \label, \tag, \nonumber, \displaystyle;
 * - особенности MathLive: \exponentialE, \differentialD, \placeholder, \mleft/\mright, \gt/\lt, \operatorname{tg};
 * - СТО: десятичная запятая (4.6.2.11), «*» → «·» и «2x3» → «2×3» (4.6.1.5), «⋯» → «…» (4.6.2.5),
 *   названия функций sin, ln… прямым шрифтом (4.6.1.10), константа e прямым шрифтом.
 * Возвращает новую строку и список сделанных исправлений.
 */
export function canon(latex: string): { latex: string; notes: CanonNote[] } {
  const notes: CanonNote[] = [];
  const note = (code: string, msg: string) => { if (!notes.some((x) => x.code === code)) notes.push({ code, msg }); };
  let s = String(latex || '').trim();
  // обёртки $…$ / $$…$$ / \[…\] / \(…\)
  for (let k = 0; k < 3; k++) {
    const m = s.match(/^\$\$([\s\S]*)\$\$$/) || s.match(/^\\\[([\s\S]*)\\\]$/) || s.match(/^\\\(([\s\S]*)\\\)$/) || s.match(/^\$([\s\S]*)\$$/);
    if (!m) break;
    s = m[1].trim();
  }
  let ns = parse(s);
  // окружения документа
  if (ns.length === 1 && ns[0].t === 'env' && WRAP_ENVS.has(ns[0].n)) {
    const e = ns[0];
    const rows = splitRows(e.b);
    ns = rows.length > 1 || e.b.some((x) => x.t === 'amp') ? [{ t: 'env', n: 'aligned', b: e.b }] : e.b;
  }
  ns = mapNodes(ns, function fix(n): N | N[] | null | undefined {
    if (n.t === 'env' && WRAP_ENVS.has(n.n)) return { t: 'env', n: 'aligned', b: mapNodes(n.b, fix) };
    if (n.t === 'env' && n.n === 'array') return { t: 'env', n: 'matrix', b: mapNodes(n.b, fix) };
    if (n.t === 'env' && (n.n === 'smallmatrix')) return { t: 'env', n: 'matrix', b: mapNodes(n.b, fix) };
    if (n.t === 'txt' && n.n === 'ce') return parse(chemToLatex(n.s));
    if (n.t === 'lr') {
      // \left\{ \begin{array|aligned|matrix} … \right. -> cases (система уравнений)
      const inner = n.b.filter((x) => x.t !== 'sp');
      if (n.l === '\\{' && (n.r === '.' || n.r === '') && inner.length === 1 && inner[0].t === 'env'
        && ['array', 'aligned', 'matrix', 'gathered', 'align', 'align*', 'split'].includes(inner[0].n)) {
        return { t: 'env', n: 'cases', b: mapNodes(inner[0].b, fix) };
      }
      return undefined;
    }
    if (n.t !== 'cmd') return undefined;
    const a0 = n.args[0] || [];
    switch (n.n) {
      case 'label': case 'tag': case 'nonumber': case 'notag': case 'displaystyle': case 'textstyle':
      case 'limits': case 'nolimits': case 'middle': case 'color': case 'allowbreak': case 'nobreak':
        return null;
      case 'placeholder': return a0.length ? mapNodes(a0, fix) : null;
      case 'textcolor': return mapNodes(n.args[1] || [], fix);
      case 'htmlData': case 'class': case 'cssId': return mapNodes(n.args[1] || [], fix);
      case 'mathord': case 'mathbin': case 'mathrel': case 'mathop': return mapNodes(a0, fix);
      case 'exponentialE': return cmd('mathrm', [c('e')]);
      case 'imaginaryI': return cmd('mathrm', [c('i')]);
      case 'imaginaryJ': return cmd('mathrm', [c('j')]);
      case 'differentialD': return cmd('mathrm', [c('d')]);
      case 'capitalDifferentialD': return cmd('mathrm', [c('D')]);
      case 'gt': return c('>');
      case 'lt': return c('<');
      case 'lbrace': return cmd('{');
      case 'rbrace': return cmd('}');
      case 'lbrack': return c('[');
      case 'rbrack': return c(']');
      case 'vert': case 'lvert': case 'rvert': return c('|');
      case 'Vert': case 'lVert': case 'rVert': return cmd('|');
      case 'colon': return c(':');
      case 'dfrac': case 'tfrac': case 'cfrac': return { ...n, n: 'frac', args: n.args.map((x) => mapNodes(x, fix)) };
      case 'dbinom': case 'tbinom': return { ...n, n: 'binom', args: n.args.map((x) => mapNodes(x, fix)) };
      case 'cdots': note('dots', 'многоточие «⋯» заменено на «…» на нижней линии (4.6.2.5)'); return cmd('ldots');
      case 'dots': case 'dotsc': case 'dotsb': case 'dotso': return cmd('ldots');
      case 'bm': case 'boldsymbol': return { ...n, n: 'mathbf', args: n.args.map((x) => mapNodes(x, fix)) };
      case 'operatorname': case 'operatorname*': case 'mathrm': {
        const t = plainText(a0);
        if (t && FUNC_NAMES.includes(t) && (STD_FUNCS.has(t) || MACRO_FUNCS.includes(t))) return cmd(t);
        if (n.n === 'operatorname*') return { ...n, n: 'operatorname' };
        return undefined;
      }
      case 'arctan': return undefined;
      case 'ast': note('mult', 'знак «*» заменён на «·» (4.6.1.5)'); return cmd('cdot');
      default: return undefined;
    }
  });
  // проходы по последовательностям: функции, десятичная запятая, умножение
  ns = seqPass(ns, note);
  let out = ser(ns);
  if (ns.some((x) => x.t === 'br')) out = normalizeLines(out);
  // пустые скобки-заполнители MathLive
  out = out.replace(/\{\s*\}(?=[\^_])/g, '{}');
  return { latex: out, notes };
}

/** Проход по каждой последовательности (включая вложенные). */
function seqPass(ns: N[], note: (c: string, m: string) => void): N[] {
  const walk = (list: N[]): N[] => {
    let arr = list.map((n) => {
      switch (n.t) {
        case 'g': return { ...n, b: walk(n.b) };
        case 'lr': return { ...n, b: walk(n.b) };
        case 'env': return { ...n, b: walk(n.b) };
        case 'cmd':
          if (n.n === 'mathrm' || n.n === 'operatorname' || n.n === 'mathbf' && false) return n;
          return { ...n, opt: n.opt ? walk(n.opt) : undefined, args: n.args.map(walk) };
        default: return n;
      }
    });
    const out: N[] = [];
    for (let i = 0; i < arr.length; i++) {
      const n = arr[i];
      // 1) слово-функция из букв подряд: sin, ln, arctg … -> \sin
      if (isLetter(n) && (i === 0 || !isLetter(arr[i - 1]))) {
        let j = i;
        let w = '';
        while (j < arr.length && isLetter(arr[j])) { w += (arr[j] as any).v; j++; }
        const fn = longestFunc(w);
        if (fn && w.startsWith(fn.name) && !(arr[j] && arr[j].t === 'c' && (arr[j] as any).v === '(' && fn.name.length <= 2 && false)) {
          out.push(cmd(fn.name));
          note('func', 'названия функций набраны прямым шрифтом (4.6.1.10)');
          // остаток слова - как был
          for (let k = i + fn.name.length; k < j; k++) out.push(arr[k]);
          i = j - 1;
          continue;
        }
      }
      // 2) десятичная точка между цифрами -> {,}
      if (n.t === 'c' && n.v === '.' && isDigit(arr[i - 1]) && isDigit(arr[i + 1])) {
        out.push({ t: 'g', b: [c(',')] });
        note('decimal', 'десятичная точка заменена запятой (4.6.2.11)');
        continue;
      }
      // 3) * -> \cdot
      if (n.t === 'c' && (n.v === '*' || n.v === '∗')) {
        out.push(cmd('cdot'));
        note('mult', 'знак «*» заменён на «·» (4.6.1.5)');
        continue;
      }
      // 4) 2x3 / 2 x 3 -> 2\times 3
      if (n.t === 'c' && /^[xхX×]$/.test(n.v) && n.v !== '×') {
        const p = prevNonSp(arr, i);
        const q = nextNonSp(arr, i);
        if (p >= 0 && q >= 0 && isDigit(arr[p]) && isDigit(arr[q])) {
          out.push(cmd('times'));
          note('mult', 'буква «x» между числами заменена знаком «×» (4.6.1.5)');
          continue;
        }
      }
      // 5) Юникод: знак минус и умножения как символы
      if (n.t === 'c' && (n.v === '−' || n.v === '–')) { out.push(c('-')); continue; }
      if (n.t === 'c' && (n.v === '⋅' || n.v === '∙')) { out.push(cmd('cdot')); continue; }
      if (n.t === 'c' && n.v === '⋯') { out.push(cmd('ldots')); note('dots', 'многоточие «⋯» заменено на «…» (4.6.2.5)'); continue; }
      out.push(n);
    }
    // константа e перед ^ (e^{x}) - прямым шрифтом (4.6.1.10). Не трогаем, если e - переменная: «e(t)», «e_i»,
    // одиночная «e» рядом или квадрат функции «e^{2}(t)» (ошибка регулирования e(t) в интегральном критерии)
    const nextAfter = (k: number) => { let j = k; while (out[j]?.t === 'sp') j++; return out[j]; };
    const eVar = out.some((n, j) => n.t === 'c' && n.v === 'e' && out[j + 1]?.t !== 'sup' && !isLetter(out[j - 1]) && !isLetter(out[j + 1]));
    for (let i = 0; i < out.length; i++) {
      const n = out[i];
      const after = nextAfter(i + 3);
      const func = !!after && ((after.t === 'c' && after.v === '(') || (after.t === 'lr' && after.l === '('));
      if (!eVar && !func && n.t === 'c' && n.v === 'e' && out[i + 1]?.t === 'sup' && !isLetter(out[i - 1]) && !isLetter(out[i + 2] && out[i + 2].t === 'c' ? out[i + 2] : undefined)) {
        out[i] = cmd('mathrm', [c('e')]);
        note('const-e', 'основание натурального логарифма e набирают прямым шрифтом (4.6.1.10)');
      }
    }
    arr = out;
    return arr;
  };
  return walk(ns);
}

function longestFunc(w: string): { name: string } | null {
  let best = '';
  for (const f of AUTO_FUNCS) if (w.startsWith(f) && f.length > best.length) best = f;
  if (!best) return null;
  // слово целиком - функция, либо функция + одна буква аргумента (sinx -> \sin x)
  if (w === best || w.length === best.length + 1) return { name: best };
  return null;
}

function prevNonSp(a: N[], i: number) { for (let k = i - 1; k >= 0; k--) if (a[k].t !== 'sp') return k; return -1; }
function nextNonSp(a: N[], i: number) { for (let k = i + 1; k < a.length; k++) if (a[k].t !== 'sp') return k; return -1; }

/** Текст из простых символов (для \operatorname{…}). */
export function plainText(ns: N[]): string {
  let s = '';
  for (const n of ns) {
    if (n.t === 'c') s += n.v;
    else if (n.t === 'sp') s += ' ';
    else if (n.t === 'g') s += plainText(n.b);
    else return '';
  }
  return s.trim();
}

function splitRows(ns: N[]): N[][] {
  const rows: N[][] = [[]];
  for (const n of ns) { if (n.t === 'br') rows.push([]); else rows[rows.length - 1].push(n); }
  return rows.filter((r) => r.some((x) => x.t !== 'sp'));
}

// ------------------------------------------------------------------------------------------------ анализ по СТО
export interface MathHint { sev: 'E' | 'W' | 'I'; code: string; msg: string; rule?: string }

/** Замечания к формуле, которые canon() не исправляет сам. */
export function hints(latex: string, opts: { inline?: boolean; numbered?: boolean } = {}): MathHint[] {
  const out: MathHint[] = [];
  const s = latex || '';
  if (!s.trim()) return out;
  if (opts.inline && isBulky(s)) {
    out.push({ sev: 'E', code: 'inline-bulky', rule: '4.6.1.1', msg: 'Громоздкая формула (дробь, сумма, интеграл, корень, матрица, предел) в строке - вынесите её в отдельную строку или запишите дробь через «/»' });
  }
  if (!opts.inline && opts.numbered && isCalcLine(s)) {
    out.push({ sev: 'W', code: 'calc-numbered', rule: '4.6.1.8', msg: 'Похоже на строку расчёта (подставлены числа, есть результат) - расчёты не нумеруют' });
  }
  if (/\\times/.test(s) && /\\cdot/.test(s)) {
    out.push({ sev: 'W', code: 'mult-mix', rule: '4.6.2.4', msg: 'В одной формуле и «·», и «×» - выберите одно обозначение умножения' });
  }
  const unk = unknownCommands(s);
  if (unk.length) out.push({ sev: 'W', code: 'unknown', msg: 'Команды, неизвестные экспорту в Word: ' + unk.map((x) => '\\' + x).join(', ') + ' - будут выведены как текст' });
  if (/\\text\{[^}]*\b(?:кг|м|с|Вт|Гц|Па|Дж|Н|В|А|Ом|мм|см|км|мс)\b[^}]*\}/.test(s) && !isCalcLine(s) && /[A-Za-zα-ω]/.test(s.replace(/\\[A-Za-z]+/g, ''))) {
    out.push({ sev: 'I', code: 'units-literal', rule: '4.6.2.6', msg: 'Единицы величин в формуле в буквенном виде не указывают (только в строке расчёта с числами)' });
  }
  const dup = /(\^\{?[^{}]*\}?)\s*\^/.test(s.replace(/\\[A-Za-z]+/g, 'X'));
  if (dup) out.push({ sev: 'W', code: 'double-sup', msg: 'Две степени подряд - заключите основание в скобки: (x^{2})^{3}' });
  return out;
}

/** Громоздкая формула (как sto_check.math_bulky): дробь, сумма/интеграл, корень, матрица, предел под знаком. */
export function isBulky(latex: string): boolean {
  return /\\(?:frac|dfrac|cfrac|binom|sum|prod|int|iint|iiint|oint|sqrt|lim|max|min|sup|inf|overset|underset|underbrace|overbrace|bigcup|bigcap)\b|\\begin\{/.test(latex || '');
}

/** Строка расчёта (4.6.1.8): после последнего «=» - число (с единицей). Повторяет sto_check.is_calc_line. */
export function isCalcLine(latex: string): boolean {
  const plain = toPlain(latex);
  if (!plain.includes('=')) return false;
  const rhs = plain.split('=').pop()!.replace(/[\s,.;]+$/, '').trim();
  // как sto_check.is_calc_line: число (× 10^n) и, возможно, единица (русская или из короткого списка латинских)
  const unit = '(?:\\s*(?:[А-Яа-яЁё°%][А-Яа-яЁё°%/·⋅\\-⁻¹²³\\d()^]*|(?:mm|cm|km|ms|kg|Hz|kHz|MHz|GHz|dB|Pa|Ω|kW|W|V|A|N|J|K|m|s)(?![A-Za-z])[/·⋅\\-⁻¹²³\\d()^]*))?';
  const num = '[−\\-+]?\\d+(?:[.,]\\d+)?(?:\\s*[·⋅×]\\s*10(?:\\^?[−\\-]?\\d*)?)?';
  return new RegExp('^(?:≈\\s*)?' + num + unit + '$').test(rhs.replace(/\s+/g, ' '));
}

/** Грубый текст формулы (для эвристик и подсказок). */
export function toPlain(latex: string): string {
  return toLinear(parse(latex), true);
}

/** Команды, которых нет ни в omml.py, ни в приведении сервера (будут выведены текстом). */
const KNOWN_CMDS = new Set([...Object.keys(ARGS), ...Object.keys(GREEK), ...Object.keys(SYM), ...FUNC_NAMES,
  ...MACRO_FUNCS, 'left', 'right', 'begin', 'end', 'quad', 'qquad', ',', ';', ':', '!', ' ', '{', '}', '|', '%', '_', '&', '#',
  '$', 'ldots', 'text', 'mathrm', 'lim', 'infty', 'deg', 'not', 'arctan', 'arccot', 'coth', 'Pr', 'gcd', 'hom', 'liminf',
  'limsup', 'cdotp', 'bmod', 'ce', 'textrm', 'textbf', 'textit', 'mbox', 'hbox', 'emptyset', 'lbrace', 'rbrace', 'enspace',
  'thinspace', 'medspace', 'thickspace', 'negthinspace', 'hspace', 'celsius', 'ohm', 'micro', 'textdegree', 'perthousand',
  'hookrightarrow', 'hookleftarrow', 'upharpoonright', 'rightharpoonup', 'Uparrow', 'Downarrow', 'nleq', 'ngeq', 'nsubseteq',
  'nparallel', 'lesssim', 'gtrsim', 'asymp', 'bowtie', 'models', 'vdash', 'dashv', 'uplus', 'sqcup', 'sqcap', 'odot',
  'ominus', 'oslash', 'wr', 'amalg', 'dag', 'ddag', 'S', 'P', 'copyright', 'pounds', 'euro', 'flat', 'natural', 'sharp',
  'clubsuit', 'diamondsuit', 'heartsuit', 'spadesuit', 'imath', 'jmath', 'Finv', 'Game', 'mho', 'eth', 'beth', 'gimel',
  'daleth', 'circledR', 'circledS', 'nmid', 'nsim', 'ncong', 'nexists', 'varkappa', 'varsigma', 'digamma', 'mathstrut',
  'strut', 'smash', 'displaylines', 'dddot', 'impliedby', 'Longleftarrow', 'longleftrightarrow', 'Rrightarrow',
  'Lleftarrow', 'nrightarrow', 'nleftarrow', 'twoheadrightarrow', 'dashrightarrow', 'leadsto', 'circlearrowleft',
  'circlearrowright', 'curvearrowleft', 'curvearrowright', 'swarrow', 'nwarrow', 'lozenge', 'blacktriangle', 'bigtriangleup',
  'bigtriangledown', 'triangleleft', 'triangleright', 'vartriangle', 'star', 'bigstar', 'sphericalangle', 'Box',
]);
/** Команды, которые экспорт в Word «упрощает» с потерей смысла (как LOSSY в stoweb/mathconv.py). */
const LOSSY_CMDS = new Set(['cancel', 'bcancel', 'xcancel', 'cancelto', 'underbrace', 'overbrace', 'boxed', 'color', 'textcolor',
  'colorbox', 'phantom', 'hphantom', 'vphantom', 'sfrac', 'SI', 'si', 'qty', 'num', 'unit']);

export function unknownCommands(latex: string): string[] {
  const out = new Set<string>();
  const walk = (ns: N[]) => {
    for (const n of ns) {
      if (n.t === 'cmd') {
        if ((!KNOWN_CMDS.has(n.n) && !/^[^A-Za-z]$/.test(n.n)) || LOSSY_CMDS.has(n.n)) out.add(n.n);
        if (n.opt) walk(n.opt);
        n.args.forEach(walk);
      } else if (n.t === 'g' || n.t === 'lr' || n.t === 'env') walk(n.b);
    }
  };
  walk(parse(latex));
  return [...out];
}

// ------------------------------------------------------------------------------------------------ символы формулы
/** Символы формулы в порядке появления (для экспликации «где …», 4.6.1.7): «x_{i}», «\bar{t}», «\rho». */
export function symbols(latex: string): string[] {
  const ns = parse(latex);
  const out: string[] = [];
  const seen = new Set<string>();
  const hasDiff = /\\int|\\frac\{\s*(?:\\mathrm\{d\}|d)|\\partial/.test(latex);
  const add = (base: N[], sub?: N[], primes = '') => {
    let s = ser(base) + (sub && sub.length ? '_{' + ser(sub) + '}' : '') + primes;
    s = s.replace(/_\{([A-Za-z0-9])\}/, '_{$1}');
    const key = s.replace(/\s+/g, '');
    if (!seen.has(key)) { seen.add(key); out.push(s); }
  };
  const isSymBase = (n: N): boolean => {
    if (n.t === 'c') return /^[A-Za-zα-ωΑ-Ωϑϕϵ]$/.test(n.v);
    if (n.t === 'cmd') {
      if (n.n in GREEK) return n.n !== 'pi';
      if (ACCENT.has(n.n) && n.args[0]) return n.args[0].some(isSymBase);
      if (n.n === 'mathbf' || n.n === 'mathit' || n.n === 'boldsymbol') return true;
      return false;
    }
    return false;
  };
  const walk = (list: N[]) => {
    const a = list.filter((x) => x.t !== 'sp');
    for (let i = 0; i < a.length; i++) {
      const n = a[i];
      if (isSymBase(n)) {
        // дифференциал d перед переменной (dx, dt) - не символ
        if (n.t === 'c' && n.v === 'd' && hasDiff && a[i + 1] && isSymBase(a[i + 1])) continue;
        let sub: N[] | undefined;
        let primes = '';
        let j = i + 1;
        while (j < a.length) {
          if (a[j].t === 'sub' && a[j + 1]) { sub = asList(a[j + 1]); j += 2; continue; }
          if (a[j].t === 'sup' && a[j + 1]) {
            const sp = asList(a[j + 1]);
            const txt = ser(sp);
            if (/^(\\prime|′|')+$/.test(txt)) { primes += "'".repeat(Math.max(1, (txt.match(/\\prime|′|'/g) || []).length)); j += 2; continue; }
            // степень - не часть символа, но её содержимое может содержать символы
            walk(sp);
            j += 2;
            continue;
          }
          if (a[j].t === 'c' && ((a[j] as any).v === "'" || (a[j] as any).v === '′')) { primes += "'"; j++; continue; }
          break;
        }
        add([n], sub && !/^[0-9]+$/.test(ser(sub)) || (sub && sub.length) ? sub : undefined, primes);
        if (sub) walk(sub.filter((x) => x.t === 'c' && false));
        i = j - 1;
        continue;
      }
      if (n.t === 'g' || n.t === 'lr') walk(n.b);
      else if (n.t === 'env') walk(n.b.filter((x) => x.t !== 'amp' && x.t !== 'br'));
      else if (n.t === 'cmd') {
        if (n.n === 'mathrm' || n.n === 'operatorname' || n.n === 'text') continue;
        if (NARY.has(n.n)) continue;
        if (n.opt) walk(n.opt);
        n.args.forEach(walk);
      } else if (n.t === 'sub' || n.t === 'sup') {
        // индексы у знаков суммы/интеграла/функций: \sum_{i=1}^{n}
        const nx = a[i + 1];
        if (nx) { walk(asList(nx)); i++; }
      }
    }
  };
  walk(ns);
  return out;
}

function asList(n: N): N[] { return n.t === 'g' ? n.b : [n]; }

/** Нормализованный ключ символа для сравнения («t_i» = «t_{i}», «\rho» = «ρ»). */
export function symKey(sym: string): string {
  let s = (sym || '').replace(/\$/g, '').trim();
  s = s.replace(/\\([A-Za-z]+)/g, (m, n) => GREEK[n] || m);
  return s.replace(/[{}\s]/g, '').replace(/\\mathrm|\\text|\\mathit/g, '');
}

// ------------------------------------------------------------------------------------------------ перенос (4.6.2.5)
// Формула с переносом хранится как \begin{aligned} … \\ … \end{aligned} (MathLive умеет её править): первая строка
// выравнивается по первому знаку соотношения («y&=…»), следующие - по первому символу («&+…»). Экспорт
// (stoweb.mathconv + writer/omml.py) делает из строк m:eqArr с теми же точками выравнивания.
export interface BreakPoint { index: number; sign: string; prio: 1 | 2 | 3; label: string }

/** Плоский список узлов формулы: тело aligned (без «&», с переносами) или верхний уровень. */
function flat(latex: string): N[] {
  const ns = parse(latex).filter((x) => x.t !== 'sp');
  if (ns.length === 1 && ns[0].t === 'env' && ['aligned', 'split', 'gathered', 'multline'].includes(ns[0].n)) {
    return ns[0].b.filter((x) => x.t !== 'sp' && x.t !== 'amp');
  }
  return ns;
}

const isRel = (n: N) => (n.t === 'c' && REL.has(n.v)) || (n.t === 'cmd' && REL_CMDS.has(n.n));

/** Строки -> aligned с точками выравнивания; одна строка - обычная формула. */
function buildLines(ns: N[]): string {
  const rows: N[][] = [[]];
  for (const n of ns) { if (n.t === 'br') rows.push([]); else rows[rows.length - 1].push(n); }
  const rs = rows.filter((r) => r.length);
  if (rs.length < 2) return ser(rs[0] || []);
  const out = rs.map((r, i) => {
    if (i > 0) return ser([{ t: 'amp' }, ...r]);
    const k = r.findIndex(isRel);
    return k < 0 ? ser([{ t: 'amp' }, ...r]) : ser([...r.slice(0, k), { t: 'amp' }, ...r.slice(k)]);
  });
  return '\\begin{aligned}' + out.join('\\\\') + '\\end{aligned}';
}

/** Места возможного переноса: 1 - знаки соотношения, 2 - «+», «−», «±», 3 - «×» (4.6.2.5; на делении нельзя). */
export function breakPoints(latex: string): BreakPoint[] {
  const ns = flat(latex);
  const out: BreakPoint[] = [];
  for (let i = 1; i < ns.length - 1; i++) {
    const n = ns[i];
    let sign = '';
    let prio: 1 | 2 | 3 | 0 = 0;
    if (isRel(n)) { sign = n.t === 'c' ? n.v : '\\' + (n as any).n; prio = 1; }
    else if (n.t === 'c' && (n.v === '+' || n.v === '-')) { sign = n.v; prio = 2; }
    else if (n.t === 'cmd' && (n.n === 'pm' || n.n === 'mp')) { sign = '\\' + n.n; prio = 2; }
    else if (n.t === 'cmd' && (n.n === 'times' || n.n === 'cdot')) { sign = '\\' + n.n; prio = 3; }
    if (!prio) continue;
    const p = ns[i - 1];
    const q = ns[i + 1];
    if (p.t === 'sup' || p.t === 'sub' || p.t === 'br' || q.t === 'br') continue;     // знак в индексе / уже перенос
    const left = toLinear(ns.slice(0, i).filter((x) => x.t !== 'br'), true).trim();
    out.push({ index: i, sign, prio: prio as 1 | 2 | 3, label: '…' + left.slice(-18) + ' ' + toLinear([n], true) });
  }
  return out;
}

/** Перенести формулу на знаке (index из breakPoints): знак остаётся в конце строки и повторяется в начале
 *  следующей; при переносе на умножении используют «×» (4.6.2.5). */
export function breakAt(latex: string, index: number): string {
  const ns = flat(latex);
  const n = ns[index];
  if (!n) return latex;
  const sign: N = n.t === 'cmd' && n.n === 'cdot' ? cmd('times') : n;
  return buildLines([...ns.slice(0, index), sign, { t: 'br' }, sign, ...ns.slice(index + 1)]);
}

/** Число строк формулы. */
export function lineCount(latex: string): number { return flat(latex).filter((x) => x.t === 'br').length + 1; }

/** Убрать переносы (повторённый знак в начале строки - тоже). */
export function removeBreaks(latex: string): string {
  const ns = flat(latex);
  const out: N[] = [];
  for (let i = 0; i < ns.length; i++) {
    if (ns[i].t === 'br') {
      const p = out[out.length - 1];
      const q = ns[i + 1];
      if (p && q && ser([p]) === ser([q])) i++;
      continue;
    }
    out.push(ns[i]);
  }
  return ser(out);
}

/** Переносы \\ на верхнем уровне (вставка из LaTeX) -> aligned с выравниванием. */
export function normalizeLines(latex: string): string {
  const ns = parse(latex).filter((x) => x.t !== 'sp');
  if (!ns.some((x) => x.t === 'br')) return latex;
  return buildLines(ns.filter((x) => x.t !== 'amp'));
}

// ------------------------------------------------------------------------------------------------ знак в конце
/** Знак препинания в конце формулы (',' '.' ';' или ''). */
export function trailingPunct(latex: string): string {
  const m = (latex || '').trim().match(/([,.;])\s*$/);
  if (!m) return '';
  // «…» и {,} - не знак препинания
  if (/\\ldots\s*$|\{,\}\s*$|\\dots\s*$/.test(latex.trim())) return '';
  return m[1];
}
export function setTrailingPunct(latex: string, p: string): string {
  let s = (latex || '').trim();
  if (trailingPunct(s)) s = s.replace(/\s*[,.;]\s*$/, '');
  return p ? s + p : s;
}

// ------------------------------------------------------------------------------------------------ строчная дробь
/** Громоздкие дроби строчной формулы -> линейная запись через «/» (4.6.1.1): \frac{a+b}{c} -> (a+b)/c. */
export function linearizeFractions(latex: string): string {
  const ns = mapNodes(parse(latex), function fix(n): N | N[] | null | undefined {
    if (n.t === 'cmd' && n.n === 'frac') {
      const [a, b] = n.args.map((x) => mapNodes(x, fix));
      const wrap = (x: N[]) => (needsParens(x) ? [{ t: 'lr', l: '(', r: ')', b: x } as N] : x);
      return [...wrap(a), c('/'), ...wrap(b)];
    }
    return undefined;
  });
  return ser(ns).replace(/\\left\(/g, '(').replace(/\\right\)/g, ')');
}

function needsParens(x: N[]): boolean {
  const a = x.filter((n) => n.t !== 'sp');
  if (a.length <= 1) return false;
  // «2x», «ab» - один множитель; знаки операций - нужны скобки
  return a.some((n) => (n.t === 'c' && /[+\-=<>±·×/]/.test(n.v)) || (n.t === 'cmd' && ['cdot', 'times', 'pm', 'mp', 'le', 'ge', 'ne', 'approx'].includes(n.n)));
}

// ------------------------------------------------------------------------------------------------ линейная запись Word
const LIN_ACC: Record<string, string> = { vec: '⃗', hat: '̂', bar: '̅', dot: '̇', ddot: '̈', tilde: '̃', widehat: '̂',
  widetilde: '̃', overline: '̅', overrightarrow: '⃗', check: '̌', breve: '̆', acute: '́', grave: '̀', mathring: '̊' };

/**
 * LaTeX -> линейная запись формул Word (UnicodeMath): a/b, x^2, x_(i+1), √(x), ∑_(i=1)^n▒x_i, ■(a&b@c&d).
 * Эту строку можно вставить в редактор формул Word (Alt+=, вставка, «Профессиональный»).
 * plain=true - упрощённый текст для подсказок (без служебных знаков Word).
 */
export function toLinear(ns: N[], plain = false): string {
  const arg = (x: N[]): string => {
    const s = toLinear(x, plain);
    const a = x.filter((n) => n.t !== 'sp');
    if (a.length <= 1 && !/[+\-=/ ]/.test(s)) return s;
    if (/^[A-Za-z0-9α-ωΑ-Ω]+$/.test(s)) return s;
    return '(' + s + ')';
  };
  let out = '';
  for (let i = 0; i < ns.length; i++) {
    const n = ns[i];
    switch (n.t) {
      case 'c': out += n.v === '-' ? '−' : n.v === '*' ? '·' : n.v; break;
      case 'sp': break;
      case 'sup': out += '^'; if (ns[i + 1]) { out += arg(asList(ns[i + 1])); i++; } break;
      case 'sub': out += '_'; if (ns[i + 1]) { out += arg(asList(ns[i + 1])); i++; } break;
      case 'amp': out += plain ? ' ' : '&'; break;
      case 'br': out += plain ? ' ' : '@'; break;
      case 'g': out += toLinear(n.b, plain); break;
      case 'txt': out += plain ? n.s : '"' + n.s + '"'; break;
      case 'lr': {
        const l = linDelim(n.l); const r = linDelim(n.r);
        out += (plain ? l : (l || '〖')) + toLinear(n.b, plain) + (plain ? r : (r || '〗'));
        break;
      }
      case 'env': {
        const rows = splitRows(n.b).map((r) => splitCells(r).map((cell) => toLinear(cell, plain)).join(plain ? ' ' : '&'));
        const body = rows.join(plain ? '; ' : '@');
        if (plain) { out += '(' + body + ')'; break; }
        const br: Record<string, [string, string]> = { pmatrix: ['(', ')'], bmatrix: ['[', ']'], vmatrix: ['|', '|'], Bmatrix: ['{', '}'], Vmatrix: ['‖', '‖'] };
        if (n.n === 'cases') out += '{█(' + body + ')┤';
        else if (n.n === 'aligned' || n.n === 'gathered' || n.n === 'split') out += '█(' + body + ')';
        else if (br[n.n]) out += br[n.n][0] + '■(' + body + ')' + br[n.n][1];
        else out += '■(' + body + ')';
        break;
      }
      case 'cmd': {
        const k = n.n;
        const A = n.args;
        if (k === 'frac') { out += arg(A[0]) + '/' + arg(A[1]); break; }
        if (k === 'binom') { out += plain ? 'C(' + toLinear(A[0], true) + ',' + toLinear(A[1], true) + ')' : '(' + toLinear(A[0]) + '¦' + toLinear(A[1]) + ')'; break; }
        if (k === 'sqrt') { out += n.opt ? (plain ? '√[' + toLinear(n.opt, true) + ']' : '√(' + toLinear(n.opt) + '&' + toLinear(A[0]) + ')') + (plain ? '(' + toLinear(A[0], true) + ')' : '') : '√' + arg(A[0]); break; }
        if (k in GREEK) { out += GREEK[k]; break; }
        if (k in SYM) {
          const ch = k === 'cdots' ? '⋯' : SYM[k];
          if (NARY.has(k) && !plain) {
            // ∑_(i=1)^n▒x : тело - следующий атом
            let s = ch;
            let j = i + 1;
            while (ns[j] && (ns[j].t === 'sp')) j++;
            while (ns[j] && (ns[j].t === 'sub' || ns[j].t === 'sup')) {
              s += (ns[j].t === 'sub' ? '_' : '^') + (ns[j + 1] ? arg(asList(ns[j + 1])) : '');
              j += 2;
            }
            out += s + '▒';
            i = j - 1;
            break;
          }
          out += ch;
          break;
        }
        if (FUNC_NAMES.includes(k)) {
          if (k === 'lim' || k === 'max' || k === 'min') {
            let j = i + 1;
            while (ns[j] && ns[j].t === 'sp') j++;
            if (ns[j] && ns[j].t === 'sub' && ns[j + 1]) {
              out += plain ? k + ' ' + toLinear(asList(ns[j + 1]), true) + ' ' : k + '┬' + arg(asList(ns[j + 1])) + '⁡';
              i = j + 1;
              break;
            }
          }
          out += k + (plain ? ' ' : '⁡');
          break;
        }
        if (k === 'mathrm' || k === 'operatorname' || k === 'mathbf' || k === 'mathit' || k === 'boldsymbol' || k === 'mathsf' || k === 'mathtt') { out += toLinear(A[0], plain); break; }
        if (k === 'mathbb') { out += dsLetters(plainText(A[0])); break; }
        if (k in LIN_ACC) { out += plain ? toLinear(A[0], true) : arg(A[0]) + LIN_ACC[k]; break; }
        if (k === 'overset') { out += plain ? toLinear(A[1], true) : arg(A[1]) + '┴' + arg(A[0]); break; }
        if (k === 'underset') { out += plain ? toLinear(A[1], true) : arg(A[1]) + '┬' + arg(A[0]); break; }
        if (k === 'underbrace') { out += plain ? toLinear(A[0], true) : '⏟' + arg(A[0]); break; }
        if (k === 'overbrace') { out += plain ? toLinear(A[0], true) : '⏞' + arg(A[0]); break; }
        if (k === 'xrightarrow') { out += plain ? '→' : '→┴' + arg(A[0]); break; }
        if (k === 'boxed') { out += plain ? toLinear(A[0], true) : '▭(' + toLinear(A[0]) + ')'; break; }
        if (k === ',' || k === ':' || k === ';' || k === ' ') { out += plain ? ' ' : ' '; break; }
        if (k === 'quad' || k === 'qquad') { out += plain ? '  ' : ' '; break; }
        if (k === '!') break;
        if (k === '{' || k === '}' || k === '%' || k === '#' || k === '&' || k === '_' || k === '$') { out += k; break; }
        if (k === '|') { out += '‖'; break; }
        if (MACRO_FUNCS.includes(k)) { out += k + (plain ? ' ' : '⁡'); break; }
        if (k === 'celsius') { out += '°C'; break; }
        if (k === 'degree' || k === 'textdegree') { out += '°'; break; }
        out += plain ? k : '\\' + k + ' ';
        break;
      }
    }
  }
  return out;
}

function linDelim(d: string): string {
  const m: Record<string, string> = { '.': '', '\\{': '{', '\\}': '}', '\\|': '‖', '\\langle': '⟨', '\\rangle': '⟩',
    '\\lceil': '⌈', '\\rceil': '⌉', '\\lfloor': '⌊', '\\rfloor': '⌋' };
  return d in m ? m[d] : d;
}

function splitCells(r: N[]): N[][] {
  const cells: N[][] = [[]];
  for (const n of r) { if (n.t === 'amp') cells.push([]); else cells[cells.length - 1].push(n); }
  return cells;
}

const DS: Record<string, string> = { N: 'ℕ', Z: 'ℤ', Q: 'ℚ', R: 'ℝ', C: 'ℂ', P: 'ℙ', H: 'ℍ' };
function dsLetters(s: string) { return [...s].map((ch) => DS[ch] || ch).join(''); }

export function latexToLinear(latex: string): string { return toLinear(parse(latex)).replace(/\s+/g, ' ').trim(); }

// ------------------------------------------------------------------------------------------------ химия (4.6.3)
/**
 * Химическая запись (как в mhchem \ce): «H2SO4 + 2NaOH -> Na2SO4 + 2H2O», «Cu^2+», «SO4^2-», «CuSO4*5H2O»
 * -> LaTeX с прямыми символами элементов, индексами и знаками реакций (4.6.3.1, 4.6.3.4, 4.6.3.5).
 */
export function chemToLatex(src: string): string {
  let s = (src || '').trim();
  const out: string[] = [];
  let i = 0;
  const ARROWS: [string, string][] = [['<=>', '\\rightleftarrows '], ['<->', '\\leftrightarrow '], ['->', '\\rightarrow '],
    ['<-', '\\leftarrow '], ['⇄', '\\rightleftarrows '], ['⇌', '\\rightleftarrows '], ['→', '\\rightarrow '], ['←', '\\leftarrow '], ['=', '=']];
  let atStart = true;               // начало частицы: цифры - коэффициент
  while (i < s.length) {
    const ch = s[i];
    const arrow = ARROWS.find(([a]) => s.startsWith(a, i));
    if (arrow) {
      // условия над стрелкой: ->[t]
      i += arrow[0].length;
      if (s[i] === '[') {
        const j = s.indexOf(']', i);
        const cond = s.slice(i + 1, j < 0 ? s.length : j);
        i = j < 0 ? s.length : j + 1;
        out.push('\\xrightarrow{\\text{' + cond + '}}');
      } else out.push(arrow[1]);
      atStart = true;
      continue;
    }
    if (/\s/.test(ch)) { i++; continue; }
    if (ch === '+' && atStart === false && (s[i + 1] === undefined || /\s/.test(s[i + 1])) && /\s/.test(s[i - 1] || ' ')) {
      out.push('+'); atStart = true; i++; continue;
    }
    if (ch === '+' && /\s/.test(s[i - 1] || ' ')) { out.push('+'); atStart = true; i++; continue; }
    if (/\d/.test(ch) && atStart) {
      let j = i; while (j < s.length && /[\d,.]/.test(s[j])) j++;
      out.push(s.slice(i, j).replace('.', '{,}'));
      i = j;
      atStart = false;
      continue;
    }
    if (/[A-Z]/.test(ch)) {
      let j = i + 1;
      while (j < s.length && /[a-z]/.test(s[j])) j++;
      out.push('\\mathrm{' + s.slice(i, j) + '}');
      i = j;
      atStart = false;
      // индекс
      if (/\d/.test(s[i] || '')) {
        let k = i; while (k < s.length && /\d/.test(s[k])) k++;
        out.push('_{' + s.slice(i, k) + '}');
        i = k;
      }
      continue;
    }
    if (ch === '(' || ch === '[') {
      // (OH)2 или состояние (aq), (s), (g), (l), (тв)
      const close = ch === '(' ? ')' : ']';
      const j = s.indexOf(close, i);
      const inner = s.slice(i + 1, j < 0 ? s.length : j);
      i = j < 0 ? s.length : j + 1;
      if (/^(aq|s|l|g|тв|ж|г|р-р)$/.test(inner)) { out.push('_{(\\mathrm{' + inner + '})}'); continue; }
      out.push('\\left' + ch + chemToLatex(inner) + '\\right' + close);
      if (/\d/.test(s[i] || '')) {
        let k = i; while (k < s.length && /\d/.test(s[k])) k++;
        out.push('_{' + s.slice(i, k) + '}');
        i = k;
      }
      atStart = false;
      continue;
    }
    if (ch === '^') {
      // заряд: ^2+, ^-, ^{3-}
      i++;
      let q = '';
      if (s[i] === '{') { const j = s.indexOf('}', i); q = s.slice(i + 1, j); i = j + 1; } else {
        let j = i; while (j < s.length && /[\d+\-−–]/.test(s[j])) j++;
        q = s.slice(i, j); i = j;
      }
      if (!q) { out.push('\\uparrow '); continue; }       // газ: «^»
      out.push('^{' + q.replace(/[-−–]/g, '-') + '}');
      continue;
    }
    if (ch === '*' || ch === '·' || ch === '•') { out.push('\\cdot '); atStart = true; i++; continue; }
    if ((ch === 'v' || ch === '↓') && (s[i + 1] === undefined || /\s/.test(s[i + 1]))) { out.push('\\downarrow '); i++; continue; }
    if (ch === '↑') { out.push('\\uparrow '); i++; continue; }
    if (ch === '-' || ch === '−' || ch === '–') {
      // заряд без ^ в конце частицы: Cl- ; или «-» как знак
      out.push(/[A-Za-z)\d]/.test(s[i - 1] || '') && !/\s/.test(s[i + 1] || ' ') ? '-' : '^{-}');
      i++;
      continue;
    }
    if (ch === '+') { out.push('^{+}'); i++; continue; }
    out.push(ch);
    i++;
  }
  // соседние символы элементов без индексов - одним прямым блоком: \mathrm{Na}\mathrm{OH} -> \mathrm{NaOH}
  let r = out.join('');
  for (let k = 0; k < 5; k++) r = r.replace(/\\mathrm\{([A-Za-z]+)\}\\mathrm\{([A-Za-z]+)\}/g, '\\mathrm{$1$2}');
  return r.replace(/\s+$/, '');
}

/** Похоже ли на химическую запись (для автоопределения формата вставки). */
export function looksChemical(s: string): boolean {
  const t = (s || '').trim();
  if (!t || /[\\$_{}]/.test(t)) return false;
  if (!/^[A-Za-z0-9()[\]+\-−=<>*·•^\s→⇄⇌↑↓]+$/.test(t)) return false;
  if (!/^[\d(]*[A-Z]/.test(t)) return false;                          // коэффициент или символ элемента
  const words = t.match(/[A-Za-z]+/g) || [];
  if (!words.every((w) => /^(?:[A-Z][a-z]?)+$/.test(w) || w === 'v')) return false;   // только символы элементов
  const arrow = /->|<=>|<->|→|⇄|⇌/.test(t);
  const twoLetter = /[A-Z][a-z]/.test(t);
  const idx = (t.match(/[A-Za-z)]\d/g) || []).length;
  return arrow || (twoLetter && idx >= 1) || idx >= 2;
}
